/**
 * /app/data-entry — manual floor data entry (universal, assignment-scoped).
 *
 * ONE page, two Chrome-style sub-tabs:
 *
 *   • Registers — grouped, ordered forms (a melt log, a sand-test register, a pour
 *     sheet). This tab is BOTH the authoring surface (model.write: create a register,
 *     add fields via the typeahead, assign operators, review submission history — the
 *     same CreateRegisterSheet / RegisterDetailSheet the standalone Registers page used)
 *     AND the fill surface: each register assigned to YOU (GET /v1/twin/my-registers)
 *     renders as one titled form; one Submit writes a submission + one reading per field
 *     at a shared timestamp (POST /v1/twin/registers/{id}/submissions), operator-scoped.
 *   • Equipment — the flat per-equipment card (GET /v1/twin/my-manual-entry-targets):
 *     one card per assigned entity, publishing to its gateway via
 *     POST /v1/things/{id}/readings — the legacy path, kept for single-equipment entry.
 *
 * Both flow through the same ingestion pipeline as scanned/streamed sources and assemble
 * into genealogy; numeric fields publish a numeric reading, discrete fields (pattern /
 * mould / casting / qc id, or a register "Text" field) publish a value_text reading. An
 * operator only sees (and the server only accepts) what's assigned to them. Web + mobile
 * read the same shapes, so the two surfaces stay in sync by construction.
 *
 * The authoring components (CreateRegisterSheet / RegisterDetailSheet, and the
 * FieldsEditor typeahead they render) are REUSED from ./registers — not re-implemented.
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Boxes, ClipboardList, FlaskConical, Lock, Plus } from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { Badge } from "@/lib/ui/badge";
import { EmptyState } from "@/lib/ui/empty-state";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import { useOrg } from "@/lib/org-context";
import {
  myManualEntry,
  myRegisters,
  registers,
  thingReadings,
  type ManualEntryTarget,
  type MyRegister,
  type ReadingInput,
  type SubmitValue,
} from "@/lib/api";
import { RegisterOcrScan } from "@/components/images/RegisterOcrScan";
import { SieveCaptureCard } from "@/components/data-entry/SieveCaptureCard";
import { CreateRegisterSheet, RegisterDetailSheet, cadenceLabel } from "./registers";
import { OrgUnits } from "@/components/units/OrgUnits";
import { useShownPrefs } from "@/lib/units/context";
import { shownKey } from "@/lib/units/display";
import { draftInShownUnits, entryUnit, entryValues, isNumericKind } from "@/lib/data-entry/values";

type EntryTab = "registers" | "equipment" | "sieve";

/**
 * The data-entry page: the org's display units under it, so a field reads in them and a number typed there is sent in
 * the unit the field stores.
 */
export function DataEntry() {
  return (
    <OrgUnits>
      <DataEntryContent />
    </OrgUnits>
  );
}

function DataEntryContent() {
  const { can } = useOrg();
  // The forms start again when the shown units move, so a number typed in one unit is never sent as another.
  const unitsKey = shownKey(useShownPrefs());
  const canCapture = can("capture.write");
  const [tab, setTab] = useState<EntryTab>("registers");

  // Route-level permission guard: data entry publishes readings (capture.write).
  // A viewer/technician deep-linking here gets a clear no-access state instead of a
  // form whose publish would 403.
  if (!canCapture) {
    return (
      <PageContainer className="max-w-3xl">
        <PageHeader
          title="Data entry"
          description="Log floor inputs for the registers and equipment assigned to you."
        />
        <Card className="mt-6">
          <CardContent className="flex items-start gap-3 py-6">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-on-surface-muted" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-on-surface">
                You don&apos;t have access to data entry
              </p>
              <p className="text-sm text-on-surface-muted">
                Logging floor readings needs the{" "}
                <span className="font-medium">capture.write</span> permission — the
                Operator role (or Member / Admin) grants it. Ask an org admin to update
                your role on the Team page.
              </p>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer className={tab === "sieve" ? "max-w-6xl" : "max-w-3xl"}>
      <PageHeader
        title="Data entry"
        description="Record the readings you are asked for on the floor: in a register (a melt log, a sand test log), against one machine, or as an AFS sieve analysis. They are kept with the plant's other readings and linked to its heats and castings."
      />

      {/* Chrome-style sub-tabs — switch the panel below without leaving the page. */}
      <div
        role="tablist"
        aria-label="Data entry sections"
        className="mt-6 flex items-center gap-1 border-b border-border"
      >
        <TabButton
          label="Registers"
          icon={ClipboardList}
          active={tab === "registers"}
          onClick={() => setTab("registers")}
        />
        <TabButton
          label="Equipment"
          icon={Boxes}
          active={tab === "equipment"}
          onClick={() => setTab("equipment")}
        />
        <TabButton
          label="Sieve analysis"
          icon={FlaskConical}
          active={tab === "sieve"}
          onClick={() => setTab("sieve")}
        />
      </div>

      <div className="mt-6">
        {tab === "registers" ? (
          <RegistersTab key={unitsKey} />
        ) : tab === "equipment" ? (
          <EquipmentTab key={unitsKey} />
        ) : (
          <SieveCaptureCard />
        )}
      </div>
    </PageContainer>
  );
}

function TabButton({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  icon: typeof ClipboardList;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "border-brand text-brand"
          : "border-transparent text-on-surface-muted hover:text-on-surface",
      )}
    >
      <Icon className="size-4" />
      {label}
    </button>
  );
}

// ── Registers sub-tab ─────────────────────────────────────────────────────────
//
// Authoring (model.write) + filling in one panel: a description line, then — for a
// model.write user — a New-register button and the org's registers to manage, then
// the operator's assigned registers to fill.

function RegistersTab() {
  const { can } = useOrg();
  const canWrite = can("model.write");
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  // The operator's assigned registers — the fill cards.
  const myRegsQuery = useQuery({
    queryKey: ["my-registers"],
    queryFn: () => myRegisters.list(),
    staleTime: 30_000,
  });
  const myRegs = myRegsQuery.data?.items ?? [];

  // The org's registers to manage — only fetched (and shown) for authors.
  const manageQuery = useQuery({
    queryKey: ["registers"],
    queryFn: () => registers.list(),
    enabled: canWrite,
    staleTime: 30_000,
  });
  const manageItems = manageQuery.data?.items ?? [];

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-on-surface-muted">
          A register is a named, ordered set of manual-entry fields filled as one form — a
          melt log, sand-test register or pour sheet.
        </p>
        {canWrite && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 size-4" />
            New register
          </Button>
        )}
      </div>

      {/* Manage — author-only list of the org's registers. */}
      {canWrite && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="size-4 text-on-surface-muted" />
            <h2 className="text-sm font-semibold text-on-surface">Manage registers</h2>
            <span className="text-xs text-on-surface-muted">
              add fields, assign operators, review history
            </span>
          </div>

          {manageQuery.isLoading ? (
            <p className="text-sm text-on-surface-muted">Loading registers…</p>
          ) : manageItems.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No registers yet"
              description="Create a register, add the fields your operators record (pour temperature, sand moisture, moulds made), and choose who fills it in. It then shows here and on their Data entry screen as one form."
              cta={
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-1.5 size-4" />
                  New register
                </Button>
              }
            />
          ) : (
            <div className="flex flex-col gap-2">
              {manageItems.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setOpenId(r.id)}
                  className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-on-surface-muted hover:bg-surface-elevated"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-on-surface">
                        {r.name}
                      </span>
                      <Badge variant="secondary" className="text-[10px] uppercase">
                        {cadenceLabel(r.cadence)}
                      </Badge>
                    </div>
                    {r.description && (
                      <span className="truncate text-xs text-on-surface-muted">
                        {r.description}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-on-surface-muted">
                    {r.field_count} field{r.field_count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Fill — the operator's assigned registers. */}
      <section className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-4 text-on-surface-muted" />
          <h2 className="text-sm font-semibold text-on-surface">Fill a register</h2>
          <span className="text-xs text-on-surface-muted">
            grouped forms assigned to you — fill and submit as one
          </span>
        </div>

        {myRegsQuery.isLoading ? (
          <p className="text-sm text-on-surface-muted">Loading your registers…</p>
        ) : myRegs.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No registers assigned to you"
            description="Registers you're assigned to fill appear here as forms. Ask an admin to assign one to you."
          />
        ) : (
          myRegs.map((reg) => <RegisterFillCard key={reg.id} reg={reg} />)
        )}
      </section>

      {createOpen && (
        <CreateRegisterSheet
          onClose={() => setCreateOpen(false)}
          onCreated={(reg) => {
            setCreateOpen(false);
            setOpenId(reg.id);
          }}
        />
      )}
      {openId && (
        <RegisterDetailSheet registerId={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

// One assigned register rendered as a fillable form. Kept as its own component so
// each card owns its draft state — filling one never disturbs another.
function RegisterFillCard({ reg }: { reg: MyRegister }) {
  const toast = useToast();
  const prefs = useShownPrefs();
  // Field values keyed by metric; the OCR scan image sent as source_image_id on submit.
  const [values, setValues] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [sourceImageId, setSourceImageId] = useState<string | undefined>(undefined);

  const submitMut = useMutation({
    mutationFn: (body: {
      values: SubmitValue[];
      note?: string;
      source_image_id?: string;
    }) => registers.submit(reg.id, body),
  });

  const onSubmit = () => {
    // A number typed in the shown unit goes in the unit the field stores (entryValues).
    const vals: SubmitValue[] = entryValues(reg.fields, (f) => values[f.metric] ?? "", prefs);
    if (vals.length === 0) {
      toast.push("info", "Fill at least one field to submit.");
      return;
    }
    const trimmedNote = note.trim();
    submitMut.mutate(
      {
        values: vals,
        note: trimmedNote || undefined,
        source_image_id: sourceImageId,
      },
      {
        onSuccess: (s) => {
          toast.push(
            "ok",
            `Submitted “${reg.name}” — ${s.value_count} value${s.value_count === 1 ? "" : "s"} logged.`,
          );
          setValues({});
          setNote("");
          setSourceImageId(undefined);
        },
        onError: () => toast.push("error", "Couldn't submit the register."),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {reg.name}
          <Badge variant="secondary" className="text-[10px] uppercase">
            {cadenceLabel(reg.cadence)}
          </Badge>
        </CardTitle>
        {reg.description && (
          <p className="text-xs text-on-surface-muted">{reg.description}</p>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-border px-3 py-2">
          <span className="text-xs text-on-surface-muted">
            Digitise a paper copy — Claude reads it into a draft you review.
          </span>
          <RegisterOcrScan
            registerId={reg.id}
            fields={reg.fields}
            onApply={(applied, imageId) => {
              // The scan reads each field in the unit it stores; the form shows the org's (#1190 review HIGH).
              setValues((v) => ({ ...v, ...draftInShownUnits(reg.fields, applied, prefs) }));
              setSourceImageId(imageId);
            }}
          />
        </div>
        {reg.fields.map((f) => {
          const inputId = `reg-${reg.id}-${f.metric}`;
          return (
            <div key={f.metric} className="flex flex-col gap-1.5">
              <Label htmlFor={inputId}>
                {f.label}
                {entryUnit(f, prefs) ? <span className="text-on-surface-muted"> ({entryUnit(f, prefs)})</span> : null}
              </Label>
              <Input
                id={inputId}
                type={isNumericKind(f.data_kind) ? "number" : "text"}
                placeholder={isNumericKind(f.data_kind) ? "value" : "id / label"}
                value={values[f.metric] ?? ""}
                onChange={(e) =>
                  setValues((v) => ({ ...v, [f.metric]: e.target.value }))
                }
              />
            </div>
          );
        })}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`reg-note-${reg.id}`}>Note (optional)</Label>
          <Input
            id={`reg-note-${reg.id}`}
            placeholder="e.g. shift B, ladle 2"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={onSubmit} disabled={submitMut.isPending}>
            {submitMut.isPending ? "Submitting…" : "Submit register"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Equipment sub-tab ─────────────────────────────────────────────────────────
//
// The flat per-equipment cards (my-manual-entry-targets) — the legacy path,
// unchanged: one card per assigned entity, publishing to its gateway.

function EquipmentTab() {
  const toast = useToast();
  const prefs = useShownPrefs();

  // Values keyed `${entity_id}|${metric}` so two entities' same-named fields never
  // collide. submitting holds the entity_id currently being published.
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);

  const targetsQuery = useQuery({
    queryKey: ["my-manual-entry-targets"],
    queryFn: () => myManualEntry.targets(),
    staleTime: 30_000,
  });
  const targets = targetsQuery.data?.items ?? [];

  const publish = useMutation({
    mutationFn: (vars: { thingId: string; readings: ReadingInput[] }) =>
      thingReadings.publish(vars.thingId, { readings: vars.readings }),
  });

  const onPublish = (t: ManualEntryTarget) => {
    // A number typed in the shown unit goes in the unit the field stores (entryValues).
    const readings: ReadingInput[] = entryValues(t.fields, (f) => values[`${t.entity_id}|${f.metric}`] ?? "", prefs);
    if (readings.length === 0) {
      toast.push("info", "Fill at least one field to log.");
      return;
    }
    setSubmitting(t.entity_id);
    publish.mutate(
      { thingId: t.thing_id, readings },
      {
        onSuccess: (r) => {
          toast.push(
            "ok",
            `Logged ${r.accepted} reading${r.accepted === 1 ? "" : "s"} for ${t.entity_name}. The heat appears on Batches once processed.`,
          );
          setValues((v) => {
            const next = { ...v };
            for (const f of t.fields) delete next[`${t.entity_id}|${f.metric}`];
            return next;
          });
          setSubmitting(null);
        },
        onError: () => {
          toast.push("error", "Couldn't log the readings.");
          setSubmitting(null);
        },
      },
    );
  };

  if (targetsQuery.isLoading) {
    return <p className="text-sm text-on-surface-muted">Loading your equipment…</p>;
  }

  if (targets.length === 0) {
    return (
      <EmptyState
        icon={Boxes}
        title="No equipment assigned to you"
        description="Equipment you're assigned to log against appears here. Configure manual-entry fields on an equipment (Twin → Process graph → open an entity → Assigned operators)."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Boxes className="size-4 text-on-surface-muted" />
        <h2 className="text-sm font-semibold text-on-surface">Equipment</h2>
        <span className="text-xs text-on-surface-muted">
          log a reading against a single equipment
        </span>
      </div>

      {targets.map((t) => (
        <Card key={`${t.entity_id}|${t.thing_id}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {t.entity_name}
              <Badge variant="secondary" className="text-[10px] uppercase">
                {t.entity_kind}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {t.fields.map((f) => {
              const key = `${t.entity_id}|${f.metric}`;
              return (
                <div key={key} className="flex flex-col gap-1.5">
                  <Label htmlFor={`de-${key}`}>
                    {f.label}
                    {entryUnit(f, prefs) ? <span className="text-on-surface-muted"> ({entryUnit(f, prefs)})</span> : null}
                  </Label>
                  <Input
                    id={`de-${key}`}
                    type={isNumericKind(f.data_kind) ? "number" : "text"}
                    placeholder={isNumericKind(f.data_kind) ? "value" : "id / label"}
                    value={values[key] ?? ""}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [key]: e.target.value }))
                    }
                  />
                </div>
              );
            })}
            <div className="flex justify-end">
              <Button
                onClick={() => onPublish(t)}
                disabled={submitting === t.entity_id}
              >
                {submitting === t.entity_id ? "Logging…" : "Log readings"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
